import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import Modal from './Modal';
import AnimatedButton from './AnimatedButton';

const IntakeFormPopup = ({ visible, onClose, onSubmit, loading = false }) => {
  const { palette } = useTheme();
  const [selectedCommitment, setSelectedCommitment] = useState(null);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');

  const commitmentOptions = [
    {
      id: 'sort-of-committed',
      title: 'Sort of Committed',
      description: 'I\'m interested but not sure if I\'m ready to fully commit',
      message: 'Starting a business takes commitment and is not for the faint of heart. We\'ll support you, but success requires dedication and persistence.',
      color: '#F59E0B', // Amber
      bgColor: 'rgba(245, 158, 11, 0.1)',
    },
    {
      id: 'committed',
      title: 'Committed',
      description: 'I\'m ready to put in the work and see where it takes me',
      message: 'Starting a business takes commitment and is not for the faint of heart. Your dedication will be the key to your success.',
      color: '#3B82F6', // Blue
      bgColor: 'rgba(59, 130, 246, 0.1)',
    },
    {
      id: 'all-in',
      title: 'All In (Recommended)',
      description: 'I\'m 100% committed to building my business, no matter what it takes',
      message: 'Congratulations! You\'re taking the first step into a difficult but incredibly rewarding path. Your commitment puts you ahead of 90% of aspiring entrepreneurs.',
      color: '#10B981', // Green
      bgColor: 'rgba(16, 185, 129, 0.1)',
    },
  ];

  const handleOptionSelect = (option) => {
    setSelectedCommitment(option);
    setMessage(option.message);
    // Don't show message screen immediately - wait for Continue button
  };

  const handleContinue = () => {
    if (selectedCommitment) {
      setShowMessage(true);
    }
  };

  const handleSubmit = () => {
    if (selectedCommitment && onSubmit) {
      onSubmit(selectedCommitment.id);
    }
  };

  const handleClose = () => {
    setSelectedCommitment(null);
    setShowMessage(false);
    setMessage('');
    if (onClose) onClose();
  };

  // Reset form state when visibility changes
  React.useEffect(() => {
    if (!visible) {
      setSelectedCommitment(null);
      setShowMessage(false);
      setMessage('');
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <Modal 
      visible={visible} 
      onClose={handleClose}
      showCloseButton={false}
      backgroundColor="white"
    >
      <View style={styles.container}>
        {!showMessage ? (
          <>
            {/* Initial Selection Screen */}
            <View style={styles.header}>
              <Text style={[styles.title, { color: '#1F2937' }]}>
                Welcome to Your Entrepreneurial Journey! 🚀
              </Text>
              <Text style={[styles.subtitle, { color: '#6B7280' }]}>
                Before we begin, we'd like to understand your level of commitment to starting your business. This helps us personalize your experience and provide the right guidance.
              </Text>
            </View>

            <View style={styles.optionsContainer}>
              {commitmentOptions.map((option) => (
                <TouchableOpacity
                  key={option.id}
                  style={[
                    styles.option,
                    {
                      borderColor: selectedCommitment?.id === option.id ? option.color : (palette.border || 'rgba(255,255,255,0.2)'),
                      backgroundColor: selectedCommitment?.id === option.id ? option.bgColor : 'transparent',
                    }
                  ]}
                  onPress={() => handleOptionSelect(option)}
                  disabled={loading}
                >
                  <View style={styles.optionHeader}>
                    <View style={[styles.radioButton, { borderColor: option.color }]}>
                      {selectedCommitment?.id === option.id && (
                        <View style={[styles.radioButtonInner, { backgroundColor: option.color }]} />
                      )}
                    </View>
                    <Text style={[styles.optionTitle, { color: '#1F2937' }]}>
                      {option.title}
                    </Text>
                  </View>
                  <Text style={[styles.optionDescription, { color: '#6B7280' }]}>
                    {option.description}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.actions}>
              <AnimatedButton
                title="Continue"
                onPress={handleContinue}
                disabled={!selectedCommitment || loading}
                loading={loading}
                style={[
                  styles.continueButton,
                  {
                    backgroundColor: selectedCommitment ? (palette.primary || '#FF0600') : (palette.border || 'rgba(255,255,255,0.2)'),
                    opacity: selectedCommitment ? 1 : 0.5,
                  }
                ]}
                textStyle={styles.continueButtonText}
              />
            </View>
          </>
        ) : (
          <>
            {/* Message Screen */}
            <View style={styles.messageContainer}>
              <View style={[styles.messageIcon, { backgroundColor: selectedCommitment.bgColor }]}>
                <Text style={[styles.messageIconText, { color: selectedCommitment.color }]}>
                  {selectedCommitment.id === 'all-in' ? '🎯' : selectedCommitment.id === 'committed' ? '💪' : '⚠️'}
                </Text>
              </View>
              
              <Text style={[styles.messageTitle, { color: '#1F2937' }]}>
                Great Choice!
              </Text>
              
              <Text style={[styles.messageText, { color: '#6B7280' }]}>
                {message}
              </Text>
              
              <View style={styles.messageActions}>
                <AnimatedButton
                  title="Let's Get Started!"
                  onPress={handleSubmit}
                  loading={loading}
                  style={[
                    styles.startButton,
                    { backgroundColor: selectedCommitment.color }
                  ]}
                  textStyle={styles.startButtonText}
                />
              </View>
            </View>
          </>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  header: {
    marginBottom: 32,
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 32,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 24,
  },
  optionsContainer: {
    gap: 16,
    marginBottom: 32,
  },
  option: {
    padding: 20,
    borderRadius: 16,
    borderWidth: 2,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  optionTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '600',
    flex: 1,
  },
  optionDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
    marginLeft: 32,
  },
  actions: {
    alignItems: 'center',
  },
  continueButton: {
    minWidth: 200,
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 25,
  },
  continueButtonText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: 'white',
  },
  messageContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  messageIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  messageIconText: {
    fontSize: 32,
  },
  messageTitle: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 16,
  },
  messageText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    paddingHorizontal: 20,
  },
  messageActions: {
    width: '100%',
    alignItems: 'center',
  },
  startButton: {
    minWidth: 250,
    paddingVertical: 16,
    paddingHorizontal: 40,
    borderRadius: 25,
  },
  startButtonText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: 'white',
  },
});

export default IntakeFormPopup;